import {BaseProps} from "screens/BaseProps";
import {FormRequirement} from "types/index";
import React, {useEffect, useMemo, useState} from "react";
import {useParams} from "react-router-dom";
import {collection, CollectionReference, doc, orderBy, where} from "firebase/firestore";
import {InProgress} from "components/index";
import EmptyList from "../Objects/EmptyList";
import {ActionType, DirectionalOrder, Entity, ProcessType, Severity} from "enums/index";
import ListContainer from "../Objects/ListContainer";
import {submitForm} from "../../utility";
import {useComponentToggler} from "hooks/index";
import NoPermission, {PermissionDisplayType} from "../../NoPermission";
import {
  allStatus,
  emptyFunction,
  enCommonLabel,
  enFileRequirementLabel,
  enTaskLabel,
} from "constants/index";
import {REQUIREMENT_TYPE} from "../index";
import DeleteDialog from "components/Dialogs/DeleteDialog";
import BulkActions from "../Objects/BulkActions";
import EmptySearchResults from "components/EmptySearchResults";
import RequirementsList from "./RequirementsList";
import {AccessType, PermissionOperationKey} from "types/Permission";
import {bulkStatusSubmitHandler, StatusSubmitHandler} from "../../utility/statusSubmitHandler";
import {Stack} from "@mui/material";
import CheckboxSwitch from "components/CheckboxSwitch";
import useCollectionWithTemplate from "hooks/useCollectionWithTemplate";
import TemplateRequirementsList from "./TemplateRequirementsList";

interface FormRequirementsListProps extends BaseProps {
  taskCollectionRef: CollectionReference;
  statusFilter: string;
  algoliaResults: any[] | null;
  formRequirementsCount: number;
  formRequirementAccess: AccessType | null;
  parentEntity: Entity;
  openRequirementDrawer: (requirement: FormRequirement, type: REQUIREMENT_TYPE) => void;
  openCreateFormRequirement: () => void;
  showHidden?: boolean;
  fromTemplates?: boolean;
}

function FormRequirementsList(props: FormRequirementsListProps) {
  const {
    taskCollectionRef,
    statusFilter,
    algoliaResults,
    formRequirementsCount,
    openCreateFormRequirement,
    openRequirementDrawer,
    formRequirementAccess,
    toastProps,
    parentEntity,
    showHidden = false,
  } = props;
  const {setToastMessage, setToastSeverity, setIsToastOpen} = toastProps!;

  const {taskId} = useParams();

  const formRequirementsRef = collection(taskCollectionRef, taskId!, "formRequirements");

  const {
    data: formRequirements,
    updateProjectConstraints,
    updateTemplateContraints
  } = useCollectionWithTemplate<FormRequirement>({colRef: formRequirementsRef});
  const [selectedRequirements, setSelectedRequirements] = useState<Set<string>>(new Set());
  const [isAllRequirementsChecked, setIsAllRequirementsChecked] = useState<boolean>(false);
  const [isDeleteDialogOpen, {open: openDeleteDialog, close: closeDeleteDialog}] = useComponentToggler(false);
  const [isHideToggleLoading, {toggle: setIsHideToggleLoading}] = useComponentToggler(false);
  const [selectMultiple, setSelectMultiple] = useState(false);

  const displayedRequirements = (algoliaResults ? algoliaResults : formRequirements) ?? [];
  const requirementWithoutTemplate = useMemo(() => {
    if (!formRequirements) return [];
    if (parentEntity === Entity.Templates) return formRequirements;
    return formRequirements.filter(requirement => !requirement.templateId);
  }, [formRequirements]);

  useEffect(() => {
    if (requirementWithoutTemplate.length === 0) return;
    setIsAllRequirementsChecked(selectedRequirements.size === requirementWithoutTemplate.length);
  }, [selectedRequirements, requirementWithoutTemplate]);

  // update constraints based on received status
  useEffect(() => {
    const constraint = [
      ...(!showHidden ? [where("hidden", "==", showHidden)] : []),
      orderBy("name", DirectionalOrder.asc),
    ];
    const newConstraints = statusFilter === allStatus ? constraint
      : [...constraint, where("formRequirementStatus", "==", statusFilter)];

    updateProjectConstraints(newConstraints);
    updateTemplateContraints(newConstraints);
  }, [statusFilter, showHidden]);

  function openChatDrawer(requirement: FormRequirement) {
    openRequirementDrawer(requirement, REQUIREMENT_TYPE.formRequirement);
  }

  function getBulkCheckboxLabel() {
    return selectedRequirements.size > 0 ?
      `${selectedRequirements.size} Selected` :
      enTaskLabel.allRequirements
  }

  function handleSelectAll(checked: boolean) {
    setSelectedRequirements(() => {
      if (checked) {
        let newSet = new Set<string>();
        displayedRequirements.forEach((requirement) => {
          if (!requirement["@id"]) return;
          if (parentEntity !== Entity.Templates && !!requirement.templateId) return;

          newSet.add(requirement["@id"]);
        });
        return newSet
      }
      return new Set();
    });
  }

  function onRequirementCheckboxToggle(checked: boolean, id: string) {
    if (!id) return;
    if (checked) {
      setSelectedRequirements((prev) => {
        const newSet = new Set(prev);
        newSet.add(id);
        return newSet;
      });
      return;
    }
    setSelectedRequirements((prev) => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
  }

  async function onDeleteSelectedRequirements() {
    let submittedData: StatusSubmitHandler<any> [] = [];
    const requirementsToDelete = Array.from(selectedRequirements)

    await Promise.all(
      requirementsToDelete.map(async (id) => {
        const docRef = doc(formRequirementsRef, id);
        const requirement = displayedRequirements.find(requirement => requirement.id === id);
        const templateId = requirement ? requirement.templateId: null;
        if (!templateId || parentEntity === Entity.Templates) {
          await submitForm(
            docRef,
            ActionType.Delete,
            (status, data, isLastUpdate) => {
              isLastUpdate && submittedData.push({status, data, isLastUpdate});
            }
          );
        }
      })
    );

    bulkStatusSubmitHandler<any>({
        data: submittedData,
        successCallback: () => successCallback(enFileRequirementLabel.deleteXRequirementsSuccess(selectedRequirements.size)),
        errorCallback: () => errorCallback(ProcessType.Delete)
      }
    );
  }

  async function toggleHideStatus() {
    setIsHideToggleLoading();
    let submittedData: StatusSubmitHandler<any>[] = [];
    selectedRequirements.forEach((id) => {
        const documentRef = doc(formRequirementsRef, id);
        submitForm(documentRef, ActionType.Update,
          (status, data, isLastUpdate) => {
            isLastUpdate && submittedData.push({status, data, isLastUpdate})
          },
          {hidden: !showHidden}
        );
      }
    );
    bulkStatusSubmitHandler<any>({
      data: submittedData,
      successCallback: () => successCallback(showHidden ? enFileRequirementLabel.unHideSuccess : enFileRequirementLabel.hideSuccess),
      errorCallback: () => errorCallback(ProcessType.Update)
    });
  }

  function successCallback(message: string) {
    closeDeleteDialog();
    setSelectedRequirements(new Set<string>());
    setToastMessage(message);
    setToastSeverity(Severity.Success);
    setIsToastOpen(true);
  }

  function errorCallback(process: ProcessType) {
    showMessage(enCommonLabel.errorProcess(process), Severity.Error);
    setSelectedRequirements(new Set<string>());
    setIsHideToggleLoading();
    closeDeleteDialog();
  }

  function showMessage(message: string, severity: Severity) {
    setToastMessage(message);
    setToastSeverity(severity);
    setIsToastOpen(true);
  }

  function deleteRequirement(reqId: string) {
    setSelectedRequirements(new Set<string>([reqId]));
    openDeleteDialog();
  }

  if (formRequirementAccess && !formRequirementAccess?.[PermissionOperationKey.View] && !props.fromTemplates)
    return (
      <ListContainer
        access={formRequirementAccess}
        entity={Entity.FormRequirement}
        onCreateButtonClick={emptyFunction}
        displayCreate={false}
        requirementsCount={formRequirementsCount}
      >
        <NoPermission fullHeight={false} displayType={PermissionDisplayType.LIST}/>
      </ListContainer>
    );

  if (displayedRequirements.length === 0)
    return (
      <EmptyList
        access={formRequirementAccess}
        entity={Entity.FormRequirement}
        onCreateButtonClick={openCreateFormRequirement}
        requirementsCount={formRequirementsCount}
      />
    );

  if (!formRequirements)
    return (
      <ListContainer
        access={formRequirementAccess}
        entity={Entity.FormRequirement}
        onCreateButtonClick={openCreateFormRequirement}
        requirementsCount={formRequirementsCount}
      >
        <InProgress/>
      </ListContainer>
    )

  if (displayedRequirements.length === 0 && (algoliaResults || statusFilter !== allStatus))
    return (
      <ListContainer
        access={formRequirementAccess}
        entity={Entity.FormRequirement}
        onCreateButtonClick={openCreateFormRequirement}
        requirementsCount={formRequirementsCount}
      >
        <EmptySearchResults entity={Entity.FormRequirement}/>
      </ListContainer>
    )

  const requirementsProps = {
    formRequirementAccess,
    parentEntity,
    displayedRequirements,
    toastProps,
    onCheckboxToggle: onRequirementCheckboxToggle,
    formRequirementsRef,
    selectedRequirements,
    deleteRequirement,
    uid: props.uid
  }

  return (
    <>
      <ListContainer
        access={formRequirementAccess}
        entity={Entity.FormRequirement}
        onCreateButtonClick={openCreateFormRequirement}
        requirementsCount={formRequirementsCount}
        sxProps={{mt: {xs: 1, sm: 1, md: 2, lg: 2}}}
      >
        <Stack direction="row" alignItems="center">
          <CheckboxSwitch isCheckboxShown={selectMultiple} toggleCheckbox={setSelectMultiple}/>
          {displayedRequirements.length > 0 && selectMultiple && (
            <BulkActions
              disabled={requirementWithoutTemplate.length === 0}
              ml={parentEntity === Entity.Templates ? 1.5 : 3.5}
              showHidden={showHidden}
              parentEntity={parentEntity}
              openDeleteDialog={openDeleteDialog}
              label={getBulkCheckboxLabel()}
              isAllRequirementsChecked={isAllRequirementsChecked}
              showBulkActionsButtons={selectedRequirements.size > 0}
              handleSelectAll={handleSelectAll}
              toggleHiddenStatus={{
                toggleLoading: isHideToggleLoading,
                toggleFunction: toggleHideStatus
              }}
            />
          )}
        </Stack>
        <TemplateRequirementsList {...requirementsProps} showCheckBox={selectMultiple}/>
        <RequirementsList
          {...requirementsProps}
          openRequirementDrawer={openChatDrawer}
          showCheckBox={selectMultiple}
        />
      </ListContainer>
      <DeleteDialog
        overlayId="delete-selected-dialog"
        confirmButtonId={`del_${selectedRequirements.size}_confirmBtn`}
        cancelButtonId={`del_${selectedRequirements.size}_cancelBtn`}
        isOpen={selectedRequirements.size > 0 && isDeleteDialogOpen}
        title={enFileRequirementLabel.deleteXRequirements(selectedRequirements.size)}
        text={enFileRequirementLabel.massDeleteConfirmationText}
        handleClose={closeDeleteDialog}
        handleConfirm={onDeleteSelectedRequirements}
      />
    </>
  )
}

export default FormRequirementsList;
